import {
  AccountPage,
  AllowedSitesPage,
  AnalyticsPage,
  ApiKeysPage,
  BillingPage,
  CapabilityDetailPage,
  WalkApprovalPage,
  ChatExperiencePage,
  ConnectAppPage,
  ConnectionsPage,
  ConversationLogsPage,
  ConversationTranscriptPage,
  DesignProfilesPage,
  EngagementPage,
  ExperimentalFeaturesPage,
  FunnelsPage,
  LimitsPage,
  MapPage,
  MonitoringPage,
  OverviewPage,
  PlaygroundPage,
  PromptProfileRoute,
  PromptsComparePage,
  PromptsListPage,
  PromptsResolvePage,
  RuleEditorPage,
  SavedReportsPage,
  SiteWalkPage,
  SiteWalkRunPage,
  StudioPage,
  SystemLogsPage,
  TaskGuidesPage,
  TeachModePage,
  TeamPage,
  ToolsCatalogPage,
  WorkspaceSettingsPage,
} from './lazy-pages';
import { inShellHubEntries } from './route-table.in-shell-hubs';
import { inShellRedirectEntries } from './route-table.in-shell-redirects';
import { onPageAuthoringRouteEntries } from './route-table.on-page-authoring';

import type { PageOrRedirectRouteEntry } from './route-table.types';

/**
 * The IN-SHELL half of the dashboard route table — every path nested under the
 * single AuthGuard + AppShell layout route. Split out of `./route-table` to keep
 * both files under the ~300-line ceiling (`.claude/rules/architecture.md`);
 * `route-table.ts` re-exports this so every consumer still imports the whole
 * table from one place.
 */
/**
 * In-shell routes — nested under the single AuthGuard + AppShell layout route so
 * the chrome mounts once and the outlet swaps the page. Order is preserved from
 * the original JSX tree for readability; router matching does not depend on it.
 */
export const inShellRouteEntries: readonly PageOrRedirectRouteEntry[] = [
  { kind: 'page', placement: 'in-shell', path: '/home', component: OverviewPage },
  // C3b (WF-4.18) — the walk's approval screen. Declared BEFORE
  // `/catalog/:capabilityId` and it MUST stay there: this array's order is the
  // <Route> order, so moving it after would make `approve` read as a capability
  // id and render a detail page for a capability that does not exist. Same trap
  // the `/prompts/resolve` pair below carries its own note about.
  { kind: 'page', placement: 'in-shell', path: '/catalog/approve', component: WalkApprovalPage },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/catalog/:capabilityId',
    component: CapabilityDetailPage,
  },
  // `/tools` — the unified Tools Catalog hub (Tools + Catalog + External MCP servers).
  { kind: 'page', placement: 'in-shell', path: '/tools', component: ToolsCatalogPage },
  // `/experience` — the operator's controls over how chat composes (D4). A route
  // of its own rather than a `/settings/workspace` card: it is the surface an
  // operator reaches for when a composition surprises them, and the kill switch
  // has to be findable without knowing it is a Labs feature.
  { kind: 'page', placement: 'in-shell', path: '/experience', component: ChatExperiencePage },
  // The unified Connections hub — MCP / federation / BYOK / origins / custom-domain.
  { kind: 'page', placement: 'in-shell', path: '/connections', component: ConnectionsPage },
  // WS1 — the profile LIST is the index; each profile gets its own URL, so it is
  // bookmarkable and shareable (the header popover it replaces could not be).
  { kind: 'page', placement: 'in-shell', path: '/prompts', component: PromptsListPage },
  // WS4/WS3 — `resolve` and `compare` are declared BEFORE the `:profileId` route
  // and MUST stay there: this array's order is the <Route> order, so moving them
  // after it would make both read as a profile id.
  { kind: 'page', placement: 'in-shell', path: '/prompts/resolve', component: PromptsResolvePage },
  { kind: 'page', placement: 'in-shell', path: '/prompts/compare', component: PromptsComparePage },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/prompts/:profileId',
    component: PromptProfileRoute,
  },
  { kind: 'page', placement: 'in-shell', path: '/connect', component: ConnectAppPage },
  { kind: 'page', placement: 'in-shell', path: '/team', component: TeamPage },
  { kind: 'page', placement: 'in-shell', path: '/monitoring', component: MonitoringPage },
  // The Analytics hub — the business reading of the agent, in four rooms
  // (Overview / Results / Usage / Demand). It ABSORBED the operator-only
  // `/impact` page (Business Success Visibility § 2.4, operator decision D-2):
  // every panel that page carried is composed into a room here, so a customer
  // gets one surface that answers "is this working?" in their own terms rather
  // than an internal dashboard they could not see. `/impact` redirects.
  //
  // Behind NO deployment gate: the hub is customer-facing by design, and each
  // panel already self-gates on its own tier and flag. The `AGENT_IMPACT_ENABLED`
  // kill switch still governs the DATA underneath, so a deployment that refuses
  // to serve it renders the hub's honest empty states — which is what let the
  // operator-only page-visibility mechanism retire with the page it gated
  // (ADR-0330 superseded): reporting a killed flag on the data is the same truth
  // from one source, where a second gate was a second copy of it.
  { kind: 'page', placement: 'in-shell', path: '/analytics', component: AnalyticsPage },
  { kind: 'page', placement: 'in-shell', path: '/map', component: MapPage },
  // The audit trail and the usage/cost log are two READINGS of one page
  // (`?tab=activity|usage`), not two surfaces. `/audit` and `/usage-logs`
  // redirect; see the redirect table for why they existed.
  { kind: 'page', placement: 'in-shell', path: '/system-logs', component: SystemLogsPage },
  // AL-5 — an event permalink. Renders the SAME page with the event's drawer
  // open, so the link lands a reader in context rather than on a bare record,
  // and closing the drawer leaves them on a working trail. `/event/` segments
  // it so an id can never be mistaken for a tab or a future sub-route.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/system-logs/event/:eventId',
    component: SystemLogsPage,
  },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/settings/workspace',
    component: WorkspaceSettingsPage,
  },
  { kind: 'page', placement: 'in-shell', path: '/settings/api-keys', component: ApiKeysPage },
  // The outbound read allowlist — the pages and domains the agent may read while
  // it answers. A SETTINGS path because it configures a boundary rather than
  // being a place work happens; the nav row sits under AGENT beside `/docs`.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/settings/allowed-sites',
    component: AllowedSitesPage,
  },
  // SYN-205 — Labs: per-workspace experimental feature toggles.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/settings/experimental',
    component: ExperimentalFeaturesPage,
  },
  { kind: 'page', placement: 'in-shell', path: '/account', component: AccountPage },
  // W3-1a (ADR-0394) — teach mode. Self-gates on the workspace's `teach-mode` Labs
  // entry (the panel renders a "not enabled" state when off), so the route is always
  // safe to reach. Not in the primary nav: the entry now defaults ON (ADR-0471), so
  // nav placement is a discoverability decision rather than a gating one.
  { kind: 'page', placement: 'in-shell', path: '/teach-mode', component: TeachModePage },
  // OF-P2-14 (ADR-0449) — site mapping. Self-gates on the server
  // `SITE_WALK_ENABLED` flag (the panel renders a "not enabled" state when off), so
  // the route is safe to reach before mapping is enabled for a deployment.
  //
  // WF-1.3 — this said "deliberately NOT in the primary nav UNTIL then", which had
  // been stale since ADR-0450 turned the flag on, and it disagreed with
  // `plan/widget-perfection/TRACKER.md` 4.9, which said deliberately NEVER on the
  // ≤6-rows guideline. Two recorded rationales for one absence is how an absence
  // stops being a decision. Reconciled to plan/walk-first D6: "Map your site"
  // becomes the FIRST ROW of the AGENT group, and `/settings/allowed-sites` moves
  // to CONNECT to hold that group's size. The move itself ships with Phase 5
  // (walk-first everywhere) rather than here, so this row is still nav-less today
  // — but by a decision with a date on it, not by a flag state that has not been
  // true for months.
  { kind: 'page', placement: 'in-shell', path: '/site-walk', component: SiteWalkPage },
  // UX-SPEC § 3 / Z-4 — the run lives on the SERVER, so it has a URL of its own
  // and the page survives a reload, another browser and another admin. `/site-walk`
  // redirects here while `activeSiteWalkRun` is non-null, so an operator who comes
  // back to the idle page during a run is taken to the run rather than being
  // offered a second one the lock would refuse.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/site-walk/runs/:id',
    component: SiteWalkRunPage,
  },
  // A17 URL rule: first-class working surfaces live at root. The per-profile editor
  // is reached only via a Design Studio profile's "Edit" (`/widget?design=<id>`).
  { kind: 'page', placement: 'in-shell', path: '/widget', component: StudioPage },
  { kind: 'page', placement: 'in-shell', path: '/playground', component: PlaygroundPage },
  // SYN-44 — `/design` is the Design Studio (profile library + adopt-your-site);
  // the per-profile editor still lives at `/widget`.
  { kind: 'page', placement: 'in-shell', path: '/design', component: DesignProfilesPage },
  // Engagement — the merge of `/starters`, `/proactive` and `/page-rules` into one
  // surface: what the assistant offers first, when it speaks first, where it
  // appears at all, and what has been authored on the rule engine. The first
  // three were one visitor experience described by three pages, and none of them
  // could answer the question an operator actually has ("what does a visitor on
  // THIS page get?") because each knew only its own axis. Their paths redirect
  // (see `route-table.in-shell-redirects.ts`).
  { kind: 'page', placement: 'in-shell', path: '/engagement', component: EngagementPage },
  // F9 — authoring a rule is a full PAGE, not a dialog. A rule is a
  // configuration object with real consequences (it can switch the agent off
  // across a site), and the modal it replaced had to hold a URL matcher, a live
  // tester, a precedence number and six override editors inside an 85vh scroll
  // box — the primary action left the fold before the author reached the second
  // axis.
  //
  // It SURVIVES the merge deliberately. `/engagement`'s composer authors the
  // three exceptions a single line can finish; an axis needing a second choice
  // (which design? which voice? which capabilities?) still opens here, and every
  // exception row's Edit is the way in. Registered after `/engagement` so the
  // merged page stays the surface's home.
  { kind: 'page', placement: 'in-shell', path: '/page-rules/new', component: RuleEditorPage },
  { kind: 'page', placement: 'in-shell', path: '/page-rules/:ruleId', component: RuleEditorPage },
  // The authoring surfaces the merged page opens rather than contains — the
  // engagement-rule editor and the whole `/experiences` tree. One group in one
  // module, because they are one topic; see that file's header.
  ...onPageAuthoringRouteEntries,
  { kind: 'page', placement: 'in-shell', path: '/settings/billing', component: BillingPage },
  // T35b / ADR-0365 — the verification-discount surface is RETIRED, not rewired:
  // the page, its feature folder and its lazy entry are deleted, so this route is
  // gone rather than redirected. Migration 043's `discount_verifications` table is
  // inert but retained; its removal trigger is a production `count(*)` of zero.
  { kind: 'page', placement: 'in-shell', path: '/limits', component: LimitsPage },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/conversation-logs',
    component: ConversationLogsPage,
  },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/conversation-logs/:conversationId',
    component: ConversationTranscriptPage,
  },
  // The two routed-IA hubs and their ranking-sensitive siblings — see
  // `route-table.in-shell-hubs.ts`.
  ...inShellHubEntries,
  // Legacy path forwards — see `route-table.in-shell-redirects.ts`.
  ...inShellRedirectEntries,
  // TG-3b (frigade-parity) — the Task Guide curation queue: review, name, publish
  // and archive the task flows composed from the discovered surface. A CUSTOMER
  // surface (no internal flag) — publishing a guide is the operator's own decision
  // about what their agent may walk a visitor through.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/task-guides',
    component: TaskGuidesPage,
  },
  // FUN-3 (`plan/frigade-parity/PLAN.md` § 4) — operator-defined funnels. Its own
  // route rather than a tab on the impact surface, which was gated on a default-OFF
  // deployment flag when this landed: a surface an operator cannot reach is a
  // surface that does not exist. That flag is gone with the page — `/impact` is now
  // the `/analytics` hub — and the route stays its own for the reason `/reports`
  // gives below: conversion is not a sub-topic of analytics reporting.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/funnels',
    component: FunnelsPage,
  },
  // REP-1 (`plan/frigade-parity/PLAN.md` § 5) — saved reports over the value-free
  // aggregates. Its own route rather than a section of `/funnels`: a report may name
  // impact and task-guide adoption, neither of which lives there, so an operator
  // would be assembling a cross-surface summary on a page called Conversion.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/reports',
    component: SavedReportsPage,
  },
];
